import { useEffect, useState } from "react";
import { api, formatCOP, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CheckCircle2, Clock, Eye } from "lucide-react";
import ReviewModal from "@/components/ReviewModal";

const StatusPill = ({ estado }) => {
  if (estado === "confirmada") return <span className="inline-flex items-center gap-1 text-xs bg-accent text-primary px-2 py-1 rounded-full"><CheckCircle2 className="w-3 h-3" /> Confirmada</span>;
  if (estado === "revision") return <span className="inline-flex items-center gap-1 text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded-full"><AlertTriangle className="w-3 h-3" /> Revision</span>;
  if (estado === "procesando") return <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full"><Clock className="w-3 h-3" /> Procesando</span>;
  return <span className="inline-flex items-center gap-1 text-xs bg-rose-50 text-rose-700 px-2 py-1 rounded-full">Error</span>;
};

export default function RemisionesPage() {
  const [items, setItems] = useState([]);
  const [reviewId, setReviewId] = useState(null);
  const [filter, setFilter] = useState("todas");

  const load = () => {
    const q = filter === "todas" ? "" : `?estado=${filter}`;
    api.get(`/remisiones${q}`).then((r) => setItems(r.data));
  };
  useEffect(() => { load(); }, [filter]);

  // Refetch cuando el director cambia el nombre del usuario — los snapshots
  // `uploaded_by`, `vendedor` y `force_vendedor` de las remisiones ya se
  // actualizaron en Mongo, pero el frontend tiene su lista en `useState` y hay
  // que forzar el reload para verlo (mar/2026).
  useEffect(() => {
    const onNameChange = () => load();
    window.addEventListener("user-name-changed", onNameChange);
    return () => window.removeEventListener("user-name-changed", onNameChange);
  }, [filter]);

  return (
    <div>
      <header className="flex items-end justify-between mb-8 gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Historial</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground mt-2">Remisiones</h1>
          <p className="text-muted-foreground mt-2">Todas las remisiones procesadas, con su estado y detalles.</p>
        </div>
        <div className="flex gap-2">
          {["todas", "revision", "confirmada", "procesando"].map((f) => (
            <Button
              key={f}
              data-testid={`filter-${f}`}
              variant={filter === f ? "default" : "outline"}
              className={filter === f ? "bg-primary hover:bg-primary/90" : ""}
              onClick={() => setFilter(f)}
            >
              {f === "todas" ? "Todas" : f.charAt(0).toUpperCase() + f.slice(1)}
            </Button>
          ))}
        </div>
      </header>

      <div className="bg-card border border-border rounded-xl beem-table">
        <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-border text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
          <div className="col-span-2">Fecha</div>
          <div className="col-span-3">Cliente</div>
          <div className="col-span-2">Ciudad</div>
          <div className="col-span-2">Vendedor</div>
          <div className="col-span-1 text-right">Total</div>
          <div className="col-span-2 text-right">Estado</div>
        </div>
        {items.length === 0 && <div className="py-12 text-center text-muted-foreground/70">Sin remisiones. Suba fotografias desde el panel.</div>}
        {items.map((r) => (
          <div key={r.id} className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-border/60 items-center hover:bg-muted/40 cursor-pointer" onClick={() => setReviewId(r.id)} data-testid={`remision-row-${r.id}`}>
            <div className="col-span-2 text-sm text-foreground/80">{r.fecha ? formatDate(r.fecha) : "-"}</div>
            <div className="col-span-3 text-sm text-foreground font-medium truncate">{r.cliente || "-"}</div>
            <div className="col-span-2 text-sm text-muted-foreground truncate">{r.ciudad || <span className="text-muted-foreground/70">—</span>}</div>
            <div className="col-span-2 text-sm text-muted-foreground truncate">{r.vendedor || "-"}</div>
            <div className="col-span-1 text-sm text-foreground font-semibold tabular-nums text-right">{r.total ? formatCOP(r.total) : "-"}</div>
            <div className="col-span-2 flex items-center justify-end gap-2">
              <StatusPill estado={r.estado} />
              <Eye className="w-4 h-4 text-muted-foreground/70" />
            </div>
          </div>
        ))}
      </div>

      {reviewId && <ReviewModal id={reviewId} onClose={() => { setReviewId(null); load(); }} />}
    </div>
  );
}
